import { useState } from 'react'
import { CalendarClock, CheckCircle2 } from 'lucide-react'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { Textarea } from '../../lib/shadcn/textarea'
import { Spinner } from '../../lib/shadcn/spinner'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { toast } from '../../lib/shadcn/sonner'
import { useUpdateOffRoadPeriod, useReturnVehicleToRoad } from '../../hooks/backend/fleet'
import { writeErrorMessage } from '../../utils/writeError'
import { OFF_ROAD_REASONS, type OffRoadRow } from '../../utils/servicing'

/**
 * Post progress on a vehicle that is off the road, or put it back on the road.
 *
 * The next update date is the first field and the only one that is pre-filled,
 * because the failure this screen exists to stop is not a wrong date, it is no
 * date at all: when this was built, all thirteen vehicles off the road had no
 * next update date and five of them had been down for over a hundred days each
 * behind an incident already marked resolved.
 */
export function OffRoadUpdateDialog({ row, onSaved }: { row: OffRoadRow; onSaved: () => void }) {
  const [open, setOpen] = useState(false)
  const [nextUpdate, setNextUpdate] = useState(row.next_update_date ?? '')
  const [expected, setExpected] = useState(row.expected_return_at?.slice(0, 10) ?? '')
  const [reasonCategory, setReasonCategory] = useState(row.reason_category ?? 'unknown')
  const [note, setNote] = useState('')
  const update = useUpdateOffRoadPeriod()

  async function save() {
    try {
      await update.trigger({
        off_road_id: row.off_road_id,
        next_update_date: nextUpdate || null,
        expected_return_at: expected ? `${expected}T12:00:00Z` : null,
        reason_category: reasonCategory || null,
        note: note.trim() || null,
      }).result
      toast.success(`${row.registration_number ?? 'Vehicle'} updated`)
      setOpen(false)
      setNote('')
      onSaved()
    } catch (err) {
      toast.error('Could not save the update', { description: writeErrorMessage(err) })
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="h-7 gap-1.5 px-2 text-xs">
          <CalendarClock className="h-3.5 w-3.5" />
          Update
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {row.registration_number ?? 'Vehicle'}, off the road {row.days_off_road} days
          </DialogTitle>
          <DialogDescription>
            {row.owner_name
              ? `${row.owner_name} earns nothing from this vehicle while it is down.`
              : 'This vehicle is earning nothing while it is down.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor={`nu-${row.off_road_id}`}>Next update due</Label>
            <Input
              id={`nu-${row.off_road_id}`}
              type="date"
              value={nextUpdate}
              onChange={(e) => setNextUpdate(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Leaving this blank shows the vehicle as having no update date, which the list ranks
              alongside an overdue one.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor={`er-${row.off_road_id}`}>Expected back</Label>
              <Input
                id={`er-${row.off_road_id}`}
                type="date"
                value={expected}
                onChange={(e) => setExpected(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`rc-${row.off_road_id}`}>Reason</Label>
              <NativeSelect
                id={`rc-${row.off_road_id}`}
                value={reasonCategory}
                onChange={(e) => setReasonCategory(e.target.value)}
              >
                {OFF_ROAD_REASONS.map((r) => (
                  <option key={r.code} value={r.code}>{r.label}</option>
                ))}
              </NativeSelect>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={`note-${row.off_road_id}`}>What has happened since</Label>
            <Textarea
              id={`note-${row.off_road_id}`}
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Assessor booked for Thursday, parts ordered, quote approved"
            />
          </div>

          {row.last_update_note && (
            <div className="rounded-md border border-border bg-background p-3">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Last note
              </p>
              <p className="text-xs text-foreground">{row.last_update_note}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={update.loading}>
            Close
          </Button>
          <Button onClick={save} disabled={update.loading}>
            {update.loading && <Spinner className="mr-2 h-4 w-4" />}
            Save update
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Closes the off-road spell. Reports the days lost, because that is the number. */
export function ReturnToRoadButton({ row, onSaved }: { row: OffRoadRow; onSaved: () => void }) {
  const [open, setOpen] = useState(false)
  const [note, setNote] = useState('')
  const ret = useReturnVehicleToRoad()

  async function confirm() {
    try {
      const res = (await ret.trigger({
        vehicle_id: row.vehicle_id,
        note: note.trim() || null,
      }).result) as { days_off_road?: number; registration_number?: string } | null
      const days = res?.days_off_road ?? row.days_off_road
      toast.success(
        `${res?.registration_number ?? row.registration_number ?? 'Vehicle'} back on the road after ${days} day${days === 1 ? '' : 's'}`,
      )
      setOpen(false)
      onSaved()
    } catch (err) {
      toast.error('Could not put the vehicle back on the road', {
        description: writeErrorMessage(err),
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="h-7 gap-1.5 px-2 text-xs">
          <CheckCircle2 className="h-3.5 w-3.5" />
          Back on road
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Put {row.registration_number ?? 'this vehicle'} back on the road?</DialogTitle>
          <DialogDescription>
            This closes a spell of {row.days_off_road} day{row.days_off_road === 1 ? '' : 's'} and
            makes the vehicle available for allocation again.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor={`rnote-${row.off_road_id}`}>Closing note</Label>
          <Textarea
            id={`rnote-${row.off_road_id}`}
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Repairs signed off, roadworthy issued"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={ret.loading}>
            Cancel
          </Button>
          <Button onClick={confirm} disabled={ret.loading}>
            {ret.loading && <Spinner className="mr-2 h-4 w-4" />}
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
